import Button from '@orn/shared-web/components/Button/Button';
import { ArrowNarrowLeftIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';

import MantleHorizontalLockupPrimary from '../logos/MantleHorizontalLockupPrimary';

import OnboardingFooter from './OnboardingFooter';
import OnboardingMarketingPanel from './OnboardingMarketingPanel';

import type { ReactNode } from 'react';

/** The form column's width, and the width its children should be seen at. */
export const CONTENT_MAX_WIDTH_PX = 440;
const STEPPER_HEIGHT_PX = 124;
const FADE_TAIL_HEIGHT_PX = 20;
const TOP_FADE_HEIGHT_PX = STEPPER_HEIGHT_PX + FADE_TAIL_HEIGHT_PX;

interface IOnboardingLayoutProps {
  /**
   * Form / step content rendered between the Mantle logo and the footer.
   * Constrained to {@link CONTENT_MAX_WIDTH_PX}px and centered vertically
   * within the left column.
   */
  children: ReactNode;
  /**
   * If provided, renders a back-button in the top-left of the form column.
   * Used by multi-step flows to navigate to the previous step.
   */
  onBack?: () => void;
  /**
   * Optional content rendered absolutely at the top of the left column —
   * used by the onboarding wizard to surface a progress stepper above the
   * vertically-centered form content.
   */
  headerSlot?: ReactNode;
  /**
   * Whether to render the Mantle wordmark above the form content. Defaults to
   * `false` — the Log in screen is the only consumer that opts in.
   */
  showLogo?: boolean;
}

/**
 * Shared two-column shell for the onboarding/login screens.
 *
 * - Left column: Mantle logo, the step content (centered), and the
 *   support-email footer — all in a single scroll container so the footer
 *   moves with the content rather than being pinned.
 * - Right column: marketing image panel (currently a placeholder). Hidden
 *   below `lg` so the left column fills the viewport on small screens.
 */
const OnboardingLayout = ({ children, onBack, headerSlot, showLogo = false }: IOnboardingLayoutProps) => {
  const hasHeader = Boolean(onBack || headerSlot);

  return (
    <div className="bg-app h-screen w-full">
      <div className="flex h-full w-full">
        <section className="relative flex h-full flex-1 flex-col overflow-y-auto">
          {hasHeader && (
            <div className="sticky top-0 z-10">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0"
                style={{
                  height: `${TOP_FADE_HEIGHT_PX}px`,
                  background: `linear-gradient(to bottom, var(--color-bg-app) ${STEPPER_HEIGHT_PX}px, transparent ${TOP_FADE_HEIGHT_PX}px)`,
                }}
              />
              {onBack && (
                <div className="absolute left-8 top-8 z-10">
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={onBack}
                    iconLeading={ArrowNarrowLeftIcon}
                    aria-label="Go back"
                  />
                </div>
              )}
              {headerSlot && <div className="relative flex justify-center">{headerSlot}</div>}
            </div>
          )}

          <div className={cx('flex flex-1 items-center justify-center px-8 pb-12', !hasHeader && 'pt-12')}>
            <div className="flex w-full flex-col items-start gap-20" style={{ maxWidth: `${CONTENT_MAX_WIDTH_PX}px` }}>
              {showLogo && (
                <MantleHorizontalLockupPrimary className="h-7 w-auto shrink-0" role="img" aria-label="Mantle" />
              )}
              <div className="flex w-full flex-col gap-8">{children}</div>
            </div>
          </div>

          <div className="px-8 pb-6 pt-2">
            <OnboardingFooter />
          </div>
        </section>

        <aside className="hidden self-stretch py-4 pr-4 lg:flex lg:flex-1">
          <OnboardingMarketingPanel />
        </aside>
      </div>
    </div>
  );
};

OnboardingLayout.displayName = 'OnboardingLayout';

export default OnboardingLayout;
